/**
 * DocumentEditDialog — the Ablage document "Bearbeiten" (manual reclassify) modal
 * (DMS docs-fix item 2b). Lets a broker fix a mis-classified/mis-filed document from the
 * inline viewer: change its **Kategorie** (`kind`) and re-assign its **linked object**
 * (to a Kunde or a Police) via `PATCH /documents/:id` (`document.update`).
 *
 * Partial-patch semantics (mirrors `PATCH /policies/:id`): only the fields the broker
 * actually changed are sent. `kind` is sent only when the selected category differs from
 * the document's current one (so re-saving never clobbers a `police_mutation` down to
 * `police_neu` just because both display as "Police"). The linked object is sent only when
 * the broker picks a new target or explicitly detaches it.
 *
 * Presentational + stateful (the `UploadPolicyDialog`/`UploadDocumentDialog` precedent): the
 * parent injects `onSubmit` (the live/mock PATCH) + the live search fns.
 *
 * [Source: apps/web/src/features/policies/components/UploadPolicyDialog.tsx] (modal + typeahead)
 * [Source: apps/api/src/modules/documents/documents-actions.controller.ts#update]
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Pencil, Search, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import type { DocumentUpdateBody } from '../../../lib/api-client';

/** The manual-reclassify Kategorie options — value ∈ the api's EDITABLE_DOCUMENT_KINDS. */
const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(Keine)' },
  { value: 'police_neu', label: 'Police' },
  { value: 'avb', label: 'AVB' },
  { value: 'schadenmeldung', label: 'Schaden' },
  { value: 'praemienrechnung', label: 'Rechnung' },
  { value: 'other', label: 'Sonstiges' },
];

/**
 * Map a stored `document.kind` (the full `document_kind` enum) to the picker value that
 * REPRESENTS it, so the select defaults sensibly and an untouched save sends no `kind`.
 * (e.g. `police_mutation` → `police_neu` = the "Police" option.)
 */
function pickerValueForKind(kind: string | null): string {
  switch (kind) {
    case 'police_neu':
    case 'police_mutation':
      return 'police_neu';
    case 'avb':
      return 'avb';
    case 'schadenmeldung':
    case 'schadenabrechnung':
      return 'schadenmeldung';
    case 'praemienrechnung':
      return 'praemienrechnung';
    case null:
      return '';
    default:
      return 'other';
  }
}

/** A selectable linked object (Kunde or Police). */
export interface LinkTargetOption {
  /** doc_link_type: 'firma' | 'person' | 'policy'. */
  linkedType: string;
  id: string;
  label: string;
  subtitle?: string | null;
}

type LinkMode = 'kunde' | 'police' | 'ohne';

function modeForLinkedType(linkedType: string): LinkMode {
  if (linkedType === 'firma' || linkedType === 'person') return 'kunde';
  if (linkedType === 'policy') return 'police';
  return 'ohne';
}

export interface DocumentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The document being edited (current values seed the form). */
  current: { id: string; kind: string | null; linkedType: string; linkedId: string | null };
  /** Optional human label of the current linked object (shown so the broker sees what it's on now). */
  currentLinkLabel?: string | null;
  /** Perform the PATCH (parent wires the live/mock call). */
  onSubmit: (patch: DocumentUpdateBody) => Promise<void>;
  /** Live Kunden typeahead. */
  onSearchKunden: (q: string) => Promise<LinkTargetOption[]>;
  /** Live Policen typeahead. */
  onSearchPolicies: (q: string) => Promise<LinkTargetOption[]>;
  /** Fired once the PATCH is accepted (parent refetches + toasts). */
  onSaved?: () => void;
}

type SubmitState = 'idle' | 'saving' | 'failed';

export function DocumentEditDialog({
  open,
  onOpenChange,
  current,
  currentLinkLabel,
  onSubmit,
  onSearchKunden,
  onSearchPolicies,
  onSaved,
}: DocumentEditDialogProps) {
  const currentKindValue = useMemo(() => pickerValueForKind(current.kind), [current.kind]);

  const [kindValue, setKindValue] = useState(currentKindValue);
  const [linkMode, setLinkMode] = useState<LinkMode>(modeForLinkedType(current.linkedType));
  const [picked, setPicked] = useState<LinkTargetOption | null>(null);
  const [linkedDirty, setLinkedDirty] = useState(false);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinkTargetOption[]>([]);
  const [searching, setSearching] = useState(false);

  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKindValue(currentKindValue);
      setLinkMode(modeForLinkedType(current.linkedType));
      setPicked(null);
      setLinkedDirty(false);
      setQuery('');
      setResults([]);
      setSearching(false);
      setState('idle');
      setErrorMsg(null);
    }
  }, [open, currentKindValue, current.linkedType]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'saving') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, state]);

  // Debounced typeahead — Kunde vs Police depending on the active mode.
  useEffect(() => {
    if (!open || linkMode === 'ohne') return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const search = linkMode === 'kunde' ? onSearchKunden : onSearchPolicies;
    const t = setTimeout(() => {
      search(q)
        .then((res) => {
          if (!cancelled) setResults(res);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, linkMode, query, onSearchKunden, onSearchPolicies]);

  function switchMode(mode: LinkMode) {
    setLinkMode(mode);
    setPicked(null);
    setQuery('');
    setResults([]);
    // Switching to "Ohne" is itself a deliberate detach; the other modes become dirty
    // only once an object is actually picked.
    setLinkedDirty(mode === 'ohne');
  }

  // Build the partial patch: kind only if the category changed; linked_* only if re-assigned.
  function buildPatch(): DocumentUpdateBody | null {
    const patch: DocumentUpdateBody = {};
    if (kindValue !== currentKindValue) {
      patch.kind = kindValue === '' ? null : kindValue;
    }
    if (linkedDirty) {
      if (linkMode === 'ohne') {
        patch.linked_type = 'other';
        patch.linked_id = null;
      } else if (picked) {
        patch.linked_type = picked.linkedType;
        patch.linked_id = picked.id;
      }
    }
    return Object.keys(patch).length > 0 ? patch : null;
  }

  const patch = buildPatch();
  const canSave = patch !== null && state !== 'saving';

  async function handleSubmit() {
    const body = buildPatch();
    if (!body) return;
    setState('saving');
    setErrorMsg(null);
    try {
      await onSubmit(body);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Änderung konnte nicht gespeichert werden.');
      setState('failed');
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl border bg-card shadow-lg">
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              <Pencil className="size-4 text-primary" />
              Dokument bearbeiten
            </h2>
            <p className="text-xs text-muted-foreground">
              Kategorie ändern und dem richtigen Kunden oder der richtigen Police zuordnen.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        <form
          id="edit-document-form"
          className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* Kategorie */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-document-kind" className="text-xs text-muted-foreground">
              Kategorie
            </label>
            <select
              id="edit-document-kind"
              value={kindValue}
              onChange={(e) => setKindValue(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Verknüpftes Objekt */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-muted-foreground">Zuordnung</label>
            {currentLinkLabel && !linkedDirty && (
              <p className="text-xs text-muted-foreground">
                Aktuell: <span className="text-foreground">{currentLinkLabel}</span>
              </p>
            )}
            <div className="flex gap-1" role="radiogroup" aria-label="Zuordnungstyp">
              {(
                [
                  ['kunde', 'Kunde'],
                  ['police', 'Police'],
                  ['ohne', 'Ohne'],
                ] as Array<[LinkMode, string]>
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={linkMode === mode}
                  onClick={() => switchMode(mode)}
                  className={
                    linkMode === mode
                      ? 'rounded-md border border-primary bg-primary/10 px-3 py-1.5 text-xs font-medium text-foreground'
                      : 'rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent'
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {linkMode !== 'ohne' &&
              (picked ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground">
                  <span className="truncate">{picked.label}</span>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-muted-foreground underline underline-offset-2 hover:no-underline"
                    onClick={() => {
                      setPicked(null);
                      setLinkedDirty(false);
                    }}
                  >
                    Ändern
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={
                        linkMode === 'kunde'
                          ? 'Kunde suchen (mind. 2 Zeichen) …'
                          : 'Police suchen (Nr. / Bezeichnung) …'
                      }
                      aria-label={linkMode === 'kunde' ? 'Kunde suchen' : 'Police suchen'}
                      className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  {query.trim().length >= 2 && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-card">
                      {searching ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Suche läuft …</div>
                      ) : results.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Keine Treffer.</div>
                      ) : (
                        <ul className="divide-y divide-border">
                          {results.map((r) => (
                            <li key={`${r.linkedType}-${r.id}`}>
                              <button
                                type="button"
                                onClick={() => {
                                  setPicked(r);
                                  setLinkedDirty(true);
                                }}
                                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-accent"
                              >
                                <span className="text-sm text-foreground">{r.label}</span>
                                {r.subtitle && (
                                  <span className="text-xs text-muted-foreground">{r.subtitle}</span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </>
              ))}
          </div>

          {state === 'failed' && errorMsg && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-sem-alert/40 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert"
            >
              <AlertTriangle className="size-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </form>

        <div className="flex items-center justify-end gap-2 border-t p-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={state === 'saving'}>
            Abbrechen
          </Button>
          <Button type="submit" form="edit-document-form" disabled={!canSave}>
            {state === 'saving' ? 'Speichert …' : 'Speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
